import { MediaImage } from "@/components/admin/MediaAsset";
import { useEffect, useState } from "react";
import { Sparkles, Copy, Loader2, Instagram, Facebook, Music2, FileText, Check, Send, LayoutTemplate, Star, ImageIcon, FileText as FileTextIcon, Share2, Images, X, ArrowLeft, ArrowRight, Video } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AIConfigForm from "./AIConfigForm";
import SocialConfigForm from "./SocialConfigForm";
import MediaGallery from "./MediaGallery";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PublishDialog from "@/components/admin/PublishDialog";
import { SocialPublishActions } from "@/components/admin/SocialPublishActions";
import { VideoUploadButton, VideoPreview } from "@/components/admin/VideoUploader";
import VideoGallery from "@/components/admin/VideoGallery";
import FrameCoverPicker from "@/components/admin/FrameCoverPicker";
import FavoriteHashtags from "@/components/admin/FavoriteHashtags";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TONES = [
  { v: "formal", l: "Formal" },
  { v: "santai", l: "Santai" },
  { v: "promosi", l: "Promosi" },
  { v: "edukatif", l: "Edukatif" },
];

const CHANNELS = [
  { key: "instagram", label: "Instagram", icon: Instagram, testid: "content-copy-instagram-button" },
  { key: "facebook", label: "Facebook", icon: Facebook, testid: "content-copy-facebook-button" },
  { key: "tiktok", label: "TikTok", icon: Music2, testid: "content-copy-tiktok-button" },
  { key: "blog", label: "Blog", icon: FileText, testid: "content-copy-blog-button" },
];

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${path}`;

export default function ContentGenerator(props) {
  return <BusinessContentGenerator key={props.activeBusiness?.id || "no-business"} {...props} />;
}

function BusinessContentGenerator({ businesses, activeBusiness, initialTemplate, role }) {
  const [mode, setMode] = useState("auto");
  const [tone, setTone] = useState("santai");
  const [topic, setTopic] = useState("");
  const [bizId, setBizId] = useState(activeBusiness?.id || "");
  const [loading, setLoading] = useState(false);
  const [outputs, setOutputs] = useState(null);
  const [copied, setCopied] = useState("");
  const [pub, setPub] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeChannel, setActiveChannel] = useState("instagram");
  const [longForm, setLongForm] = useState(false);

  const [socialImages, setSocialImages] = useState([]);
  const [socialVideo, setSocialVideo] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoCover, setVideoCover] = useState("");
  const [videoCaption, setVideoCaption] = useState("");
  const [genReels, setGenReels] = useState(false);
  const [videoGalleryOpen, setVideoGalleryOpen] = useState(false);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [framePickerOpen, setFramePickerOpen] = useState(false);

  const attachVideo = (v) => { setSocialVideo(v); setVideoCover(""); setVideoCaption(""); setSocialImages([]); setSlideCaptions({}); };
  const removeVideo = () => { setSocialVideo(null); setVideoCover(""); setVideoCaption(""); };
  const genReelsCaption = async () => {
    const baseText = outputs?.instagram || outputs?.facebook || outputs?.[activeChannel] || "";
    setGenReels(true);
    try {
      const r = await api.post("/content/reels-caption", {
        business_id: publishBizId || bizId || null,
        context: `${topic}\n${baseText}\nVideo: ${socialVideo?.name || ""}`.slice(0, 900),
      });
      setVideoCaption(r.data.caption || "");
      toast.success("Caption Reels dibuat AI");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenReels(false); }
  };
  const [slideCaptions, setSlideCaptions] = useState({});
  const [socialDragIdx, setSocialDragIdx] = useState(null);
  const [socialCfgOpen, setSocialCfgOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [postingSocial, setPostingSocial] = useState("");

  const toggleSocialImage = (url) => {
    if (socialImages.includes(url)) { setSocialImages(socialImages.filter((x) => x !== url)); return; }
    if (socialImages.length >= 10) { toast.error("Maksimal 10 gambar per posting"); return; }
    setSocialImages([...socialImages, url]);
  };

  const composeSocialText = (baseText) => {
    const parts = socialImages
      .map((u, i) => {
        const c = (slideCaptions[u] || "").trim();
        return c ? `Slide ${i + 1}: ${c}` : null;
      })
      .filter(Boolean);
    return parts.length ? `${baseText}\n\n${parts.join("\n")}` : baseText;
  };

  const [genSlides, setGenSlides] = useState(false);
  const [slideStyle, setSlideStyle] = useState("storytelling");
  const genSlideCaptions = async () => {
    if (!socialImages.length) { toast.error("Pilih gambar dulu"); return; }
    const baseText = outputs?.instagram || outputs?.facebook || outputs?.[activeChannel] || "";
    setGenSlides(true);
    try {
      const r = await api.post("/content/slide-captions", {
        business_id: publishBizId || bizId || null,
        context: `${topic}\n${baseText}`.slice(0, 800),
        count: socialImages.length,
        style: slideStyle,
      });
      const caps = r.data.captions || [];
      setSlideCaptions((prev) => {
        const n = { ...prev };
        socialImages.forEach((u, i) => { if (caps[i]) n[u] = caps[i]; });
        return n;
      });
      toast.success("Caption slide dibuat AI");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenSlides(false); }
  };

  const buildSocialText = (targets) => {
    if (socialVideo && videoCaption.trim()) {
      if (targets.includes("instagram") && videoCaption.length > 2200) { toast.error("Caption Instagram maksimal 2200 karakter"); return null; }
      return videoCaption;
    }
    const baseText = (targets.includes("instagram") ? (outputs?.instagram || outputs?.facebook) : (outputs?.facebook || outputs?.instagram)) || outputs?.[activeChannel] || "";
    if (!baseText.trim()) { toast.error("Generate konten dulu sebelum posting"); return null; }
    if (targets.includes("instagram") && socialImages.length === 0 && !socialVideo) { toast.error("Instagram wajib menyertakan gambar atau video"); return null; }
    const text = socialVideo ? baseText : composeSocialText(baseText);
    if (targets.includes("instagram") && text.length > 2200) { toast.error(`Caption terlalu panjang untuk Instagram (${text.length}/2200 karakter) — ringkas caption slide`); return null; }
    return text;
  };

  useEffect(() => {
    if (!initialTemplate) return;
    setMode("manual");
    setTopic(initialTemplate.text || "");
    if (initialTemplate.channel === "blog") setLongForm(true);
    if (CHANNELS.some((c) => c.key === initialTemplate.channel)) setActiveChannel(initialTemplate.channel);
    const car = initialTemplate.carousel;
    if (car?.images?.length) {
      setSocialImages(car.images);
      const caps = {};
      car.images.forEach((u, i) => { const c = car.captions?.[i]; if (c) caps[u] = c; });
      setSlideCaptions(caps);
      toast.success(`Draf carousel: ${car.images.length} gambar + caption slide siap — klik Generate lalu posting`);
    }
  }, [initialTemplate]);

  useEffect(() => { api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const rankedTemplates = [...templates].sort(
    (a, b) => (b.rating_avg || 0) - (a.rating_avg || 0) || (b.usage_count || 0) - (a.usage_count || 0)
  );
  const topTemplateId = rankedTemplates.find((t) => (t.rating_avg || 0) >= 4)?.id;

  const publishBizId = (mode === "auto" ? bizId || activeBusiness?.id : activeBusiness?.id) || null;

  const generate = async () => {
    if (!publishBizId) { toast.error("Pilih bisnis terlebih dahulu"); return; }
    setLoading(true);
    setOutputs(null);
    setSuggestions([]);
    try {
      const r = await api.post("/content/generate", {
        business_id: publishBizId,
        mode,
        tone,
        topic,
        long_form: longForm,
      });
      setOutputs(r.data.outputs);
      toast.success("Konten berhasil dibuat");
      const bizName = businesses.find((x) => x.id === publishBizId)?.name || "";
      api.get("/media/suggest", { params: { text: `${topic} ${bizName} ${tone}` } })
        .then((res) => setSuggestions(res.data || []))
        .catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success(`Caption ${key} disalin`);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="grid min-w-0 grid-cols-1 xl:grid-cols-5 gap-6">
      {/* Controls */}
      <div className="xl:col-span-2 bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 h-fit">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles size={18} className="text-[#2563EB]" />
          <h2 className="font-heading font-bold">Generator Konten</h2>
        </div>

        <Tabs value={mode} onValueChange={(next) => { setMode(next); setOutputs(null); setPub(null); setSuggestions([]); }} className="mb-5">
          <TabsList className="bg-[#F1F5F9] w-full h-auto flex-wrap gap-1">
            <TabsTrigger disabled={loading} value="auto" data-testid="content-mode-auto-tab" className="flex-1 data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Otomatis</TabsTrigger>
            <TabsTrigger disabled={loading} value="manual" data-testid="content-mode-manual-tab" className="flex-1 data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Manual</TabsTrigger>
          </TabsList>
          <TabsContent value="auto" className="mt-4">
            <Label className="text-xs text-slate-600">Pilih Bisnis (sumber produk)</Label>
            <Select disabled={loading || !!postingSocial || genSlides || videoUploading || genReels} value={bizId || activeBusiness?.id || ""} onValueChange={(id) => { setBizId(id); removeVideo(); setOutputs(null); setPub(null); setSuggestions([]); setSocialImages([]); setSlideCaptions({}); }}>
              <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="content-business-select"><SelectValue placeholder="Pilih bisnis" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">Konten akan dibuat dari data produk bisnis terpilih.</p>
          </TabsContent>
          <TabsContent value="manual" className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-slate-600">Brief / Topik</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" data-testid="content-template-trigger" className="text-xs text-[#2563EB] hover:underline flex items-center gap-1"><LayoutTemplate size={12} /> Pakai Template</button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-h-72 overflow-y-auto">
                  {rankedTemplates.length === 0 && <div className="px-2 py-2 text-xs text-slate-500">Belum ada template</div>}
                  {rankedTemplates.map((t) => (
                    <DropdownMenuItem key={t.id} onSelect={() => { setTopic(t.text); api.post(`/templates/${t.id}/use`).catch(() => {}); toast.success(`Template "${t.title}" diterapkan`); }} data-testid={`content-template-${t.id}`} className="cursor-pointer focus:bg-[#F1F5F9]">
                      <span className="flex items-center gap-1.5 truncate">
                        <span className="truncate mr-1">{t.title}{t.category ? ` · ${t.category}` : ""}</span>
                        {t.id === topTemplateId && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F59E0B]/20 text-[#B45309] shrink-0" data-testid={`content-template-reco-${t.id}`}>Rekomendasi</span>}
                        {(t.rating_avg || 0) > 0 && (
                          <span className="flex items-center gap-0.5 text-[11px] text-slate-600 shrink-0" data-testid={`content-template-score-${t.id}`}>
                            <Star size={11} className="text-[#B45309] fill-[#F59E0B]" />{t.rating_avg}
                          </span>
                        )}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={4} data-testid="content-topic-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Contoh: promo diskon akhir tahun untuk produk kopi, target anak muda..." />
          </TabsContent>
        </Tabs>

        <Label className="text-xs text-slate-600">Tone / Gaya Bahasa</Label>
        <Select value={tone} onValueChange={setTone}>
          <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 mb-5" data-testid="content-tone-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
            {TONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-between gap-3 mb-5 bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-3">
          <div className="flex items-start gap-2">
            <FileTextIcon size={15} className="text-[#B45309] mt-0.5" />
            <div>
              <p className="text-sm text-slate-800">Artikel panjang</p>
              <p className="text-[11px] text-slate-500 leading-snug">Artikel blog 800–1200 kata dengan model AI bisnis terpilih.</p>
            </div>
          </div>
          <Switch checked={longForm} onCheckedChange={setLongForm} data-testid="content-longform-toggle" className="data-[state=checked]:bg-[#F59E0B]" />
        </div>

        <Button onClick={generate} disabled={loading} data-testid="content-generate-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
          {loading ? <Loader2 size={16} className="animate-spin mr-1" /> : <Sparkles size={16} className="mr-1" />}
          {outputs ? "Regenerate" : "Generate Konten"}
        </Button>
      </div>

      {/* Output */}
      <div className="min-w-0 xl:col-span-3">
        {!outputs && !loading && (
          <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center h-full flex flex-col items-center justify-center">
            <Sparkles size={32} className="text-slate-600 mb-3" />
            <p className="text-slate-600">Hasil konten akan muncul di sini.</p>
          </div>
        )}
        {loading && (
          <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center h-full flex flex-col items-center justify-center">
            <Loader2 size={32} className="text-[#2563EB] animate-spin mb-3" />
            <p className="text-slate-600">Sedang membuat konten...</p>
          </div>
        )}
        {outputs && (
          <>
            <Tabs value={activeChannel} onValueChange={setActiveChannel}>
              <TabsList className="bg-[#F1F5F9] w-full h-auto flex-wrap gap-1">
                {CHANNELS.map((c) => (
                  <TabsTrigger key={c.key} value={c.key} className="flex-1 data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">
                    <c.icon size={15} className="mr-1" />{c.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {CHANNELS.map((c) => (
                <TabsContent key={c.key} value={c.key} className="mt-4">
                  <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <span className="text-xs font-mono-code uppercase tracking-wider text-slate-500">{c.label} · {(outputs[c.key] || "").length} karakter</span>
                      <div className="flex gap-2">
                        <Button onClick={() => copy(c.key, outputs[c.key])} data-testid={c.testid} variant="outline" size="sm" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-lg text-xs">
                          {copied === c.key ? <Check size={14} className="mr-1 text-[#059669]" /> : <Copy size={14} className="mr-1" />}
                          {copied === c.key ? "Tersalin" : "Copy"}
                        </Button>
                        {role !== "operator" && <Button onClick={() => setPub({ businessId: publishBizId, channel: c.key, text: outputs[c.key] })} size="sm" className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-lg text-xs" data-testid={`content-publish-${c.key}-button`}>
                          <Send size={14} className="mr-1" /> Kirim/Jadwal
                        </Button>}
                      </div>
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed max-h-[420px] overflow-y-auto">{outputs[c.key]}</div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            {role !== "operator" && suggestions.length > 0 && (
              <div className="mt-4 bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4" data-testid="content-suggested-images">
                <p className="text-xs text-slate-600 mb-3 flex items-center gap-1.5">
                  <ImageIcon size={13} className="text-[#2563EB]" />
                  Gambar disarankan dari galeri — klik untuk lampirkan &amp; kirim/jadwal ({activeChannel}):
                </p>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {suggestions.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      title={`${m.folder || "Umum"} · ${m.original_filename || "gambar"}`}
                      onClick={() => setPub({ businessId: publishBizId, channel: activeChannel, text: outputs[activeChannel], imageUrl: fileUrl(m.storage_path) })}
                      data-testid={`content-suggested-image-${m.id}`}
                      className="group relative shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-[#E2E8F0] hover:border-[#3B82F6] transition-colors"
                    >
                      <MediaImage src={fileUrl(m.storage_path)} alt={m.original_filename || "saran"} className="w-full h-full object-cover" />
                      <span className="absolute inset-0 bg-[#2563EB]/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Send size={16} className="text-white" />
                      </span>
                      <span className="absolute bottom-0 inset-x-0 bg-[#F8FAFC]/80 text-[9px] text-slate-700 px-1 truncate">{m.folder || "Umum"}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {/* Post ke Sosial Media (Meta Graph API) */}
        {outputs && role !== "operator" && (
          <div className="mt-4 bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4" data-testid="social-post-section">
            <div className="flex items-center gap-2 mb-2">
              <Share2 size={15} className="text-[#3B82F6]" />
              <p className="text-xs font-semibold text-slate-800">Post ke Sosial Media</p>
              <div className="flex-1" />
              <button type="button" onClick={() => setSocialCfgOpen(true)} data-testid="social-config-open" className="text-[11px] text-[#2563EB] hover:underline flex items-center gap-1">
                <Share2 size={11} /> Atur Integrasi
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">Memakai teks channel Facebook/Instagram yang sedang digenerate. Instagram wajib menyertakan gambar atau video (Reels); Facebook bisa teks saja.</p>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Button type="button" variant="outline" onClick={() => setGalleryOpen(true)} disabled={!!socialVideo || videoUploading} data-testid="pick-image-button" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-xs">
                <Images size={14} className="mr-1" /> Pilih Gambar
              </Button>
              <VideoUploadButton disabled={socialImages.length > 0} onUploadingChange={setVideoUploading} onUploaded={attachVideo} />
              <Button type="button" variant="outline" onClick={() => setVideoGalleryOpen(true)} disabled={socialImages.length > 0 || videoUploading} data-testid="pick-video-gallery-button" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-xs">
                <Video size={14} className="mr-1" /> Galeri Video
              </Button>
              <Button type="button" variant="outline" onClick={genSlideCaptions} disabled={genSlides || socialImages.length === 0} data-testid="gen-slide-captions-button" className="border-[#7C3AED]/40 bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 text-[#7C3AED] rounded-xl text-xs disabled:opacity-40">
                {genSlides ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                Caption Slide AI
              </Button>
              <Select value={slideStyle} onValueChange={setSlideStyle}>
                <SelectTrigger data-testid="slide-style-select" className="bg-[#F1F5F9] border-[#E2E8F0] rounded-xl text-xs h-9 w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                  <SelectItem value="storytelling" data-testid="slide-style-storytelling">Storytelling</SelectItem>
                  <SelectItem value="edukasi" data-testid="slide-style-edukasi">Edukasi</SelectItem>
                  <SelectItem value="testimoni" data-testid="slide-style-testimoni">Testimoni</SelectItem>
                  <SelectItem value="bts" data-testid="slide-style-bts">Behind The Scene</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[11px] text-slate-500" data-testid="pick-image-count">
                {socialVideo ? "1 video dilampirkan" : socialImages.length ? `${socialImages.length} gambar dipilih` : "Belum ada gambar/video — Facebook tetap bisa posting teks saja"}
              </span>
            </div>
            {socialVideo && (
              <VideoPreview
                video={socialVideo}
                onRemove={removeVideo}
                cover={videoCover}
                onPickCover={() => setCoverPickerOpen(true)}
                onPickFrame={() => setFramePickerOpen(true)}
                extra={<FavoriteHashtags businessId={publishBizId || bizId || null} canEdit={role !== "operator"} />}
                onRemoveCover={() => setVideoCover("")}
                caption={videoCaption}
                onCaption={setVideoCaption}
                onGenCaption={genReelsCaption}
                genLoading={genReels}
              />
            )}
            {socialImages.length > 0 && (
              <div className="mb-3" data-testid="social-image-previews">
                <div className="flex items-start gap-2 overflow-x-auto pb-2">
                  {socialImages.map((url, i) => (
                    <div key={url} className="shrink-0 w-36" data-testid={`social-preview-${i}`}>
                      <div
                        draggable
                        onDragStart={() => setSocialDragIdx(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (socialDragIdx === null || socialDragIdx === i) return;
                          const arr = [...socialImages];
                          const [moved] = arr.splice(socialDragIdx, 1);
                          arr.splice(i, 0, moved);
                          setSocialImages(arr);
                          setSocialDragIdx(null);
                        }}
                        className={`relative w-full h-20 rounded-xl overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-opacity ${socialDragIdx === i ? "opacity-40 border-[#7C3AED]" : "border-[#3B82F6]"}`}
                      >
                        <MediaImage src={url} alt={`gambar ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                        <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-[#3B82F6] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSocialImages(socialImages.filter((x) => x !== url));
                            setSlideCaptions((prev) => { const n = { ...prev }; delete n[url]; return n; });
                          }}
                          data-testid={`social-preview-remove-${i}`}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#F8FAFC]/80 text-slate-800 hover:text-[#E11D48] flex items-center justify-center"
                        ><X size={11} /></button>
                      </div>
                      <div className="flex justify-between mt-1">
                        <button type="button" aria-label={`Geser slide ${i + 1} ke kiri`} disabled={i === 0} onClick={() => setSocialImages((items) => { const next = [...items]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; return next; })} className="p-2 disabled:opacity-30"><ArrowLeft size={14} /></button>
                        <button type="button" aria-label={`Geser slide ${i + 1} ke kanan`} disabled={i === socialImages.length - 1} onClick={() => setSocialImages((items) => { const next = [...items]; [next[i], next[i + 1]] = [next[i + 1], next[i]]; return next; })} className="p-2 disabled:opacity-30"><ArrowRight size={14} /></button>
                      </div>
                      <Textarea
                        value={slideCaptions[url] || ""}
                        onChange={(e) => setSlideCaptions((prev) => ({ ...prev, [url]: e.target.value }))}
                        rows={2}
                        maxLength={300}
                        placeholder={`Caption slide ${i + 1} (opsional)`}
                        data-testid={`social-preview-caption-${i}`}
                        className="mt-1.5 bg-[#F1F5F9] border-[#E2E8F0] text-[11px] resize-none"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => { setSocialImages([]); setSlideCaptions({}); }}
                    data-testid="social-image-clear"
                    className="shrink-0 self-start px-3 h-20 rounded-xl border border-[#E2E8F0] text-[11px] text-slate-500 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors"
                  >
                    Hapus semua
                  </button>
                </div>
                <p className="text-[10px] text-slate-500" data-testid="slide-caption-hint">
                  Seret gambar untuk mengubah urutan slide. Caption slide digabung ke caption utama dengan penomoran (Slide 1, Slide 2, ...) — Meta tidak mendukung caption terpisah per slide carousel.
                </p>
              </div>
            )}
            {suggestions.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-3">
                {suggestions.map((m) => {
                  const url = fileUrl(m.storage_path);
                  const on = socialImages.includes(url);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleSocialImage(url)} disabled={!!socialVideo || videoUploading}
                      data-testid={`social-image-${m.id}`}
                      title={m.original_filename || "gambar"}
                      className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${on ? "border-[#3B82F6]" : "border-transparent hover:border-[#3B82F6]/50"}`}
                    >
                      <MediaImage src={url} alt={m.original_filename || "gambar"} className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
            )}
            <SocialPublishActions key={publishBizId} buildText={buildSocialText} images={socialImages} videoUrl={socialVideo?.url || ""} coverUrl={socialVideo ? videoCover : ""} businessId={publishBizId || bizId || null} canPost={role !== "operator"} disabled={videoUploading || genReels || genSlides} onBusyChange={setPostingSocial} />
          </div>
        )}
      </div>
      <Dialog open={socialCfgOpen} onOpenChange={setSocialCfgOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-lg max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto" data-testid="social-config-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Integrasi &amp; AI Bisnis</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Model AI, API key, dan konfigurasi Facebook &amp; Instagram khusus bisnis ini — tersimpan terenkripsi.</DialogDescription>
          </DialogHeader>
          {publishBizId ? (
            <div className="space-y-4">
              <AIConfigForm businessId={publishBizId} />
              <SocialConfigForm businessId={publishBizId} />
            </div>
          ) : (
            <p className="text-xs text-slate-500" data-testid="social-config-no-biz">Pilih bisnis terlebih dahulu untuk mengatur integrasi sosial media.</p>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-3xl max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Pilih Gambar dari Galeri</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Pilih satu atau lebih gambar (maks. 10). Urutan klik menentukan urutan tampil saat diposting.</DialogDescription>
          </DialogHeader>
          <MediaGallery picker multi onPick={(urls) => { setSocialImages(urls); setSlideCaptions((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => urls.includes(k)))); setGalleryOpen(false); toast.success(`${urls.length} gambar dilampirkan`); }} />
        </DialogContent>
      </Dialog>
      <Dialog open={videoGalleryOpen} onOpenChange={setVideoGalleryOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="video-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Pilih dari Galeri Video</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Pakai ulang video yang sudah pernah diupload tanpa upload lagi.</DialogDescription>
          </DialogHeader>
          <VideoGallery picker onPick={(v) => { attachVideo(v); setVideoGalleryOpen(false); toast.success("Video dilampirkan"); }} />
        </DialogContent>
      </Dialog>
      <FrameCoverPicker open={framePickerOpen} onClose={() => setFramePickerOpen(false)} videoUrl={socialVideo?.url || ""} onPick={(url) => { setVideoCover(url); setFramePickerOpen(false); }} />
      <Dialog open={coverPickerOpen} onOpenChange={setCoverPickerOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cover-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Pilih Cover Video</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Gunakan gambar dari Galeri Media sebagai thumbnail Reels/video. Rasio 9:16 paling ideal untuk Reels.</DialogDescription>
          </DialogHeader>
          <MediaGallery picker onPick={(url) => { setVideoCover(url); setCoverPickerOpen(false); toast.success("Cover dipilih"); }} />
        </DialogContent>
      </Dialog>
      {pub && (
        <PublishDialog
          open={!!pub}
          onClose={() => setPub(null)}
          businessId={pub.businessId}
          channel={pub.channel}
          text={pub.text}
          initialImageUrl={pub.imageUrl || ""}
        />
      )}
    </div>
  );
}
